import { Badge, Card, Group, Paper, Progress, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import {
  inferColorVisionSubtype,
  toColorVisionCorrectnessArray,
  toColorVisionDetailedResults,
} from "@utils/test-specific-utils/ColorVisionTestUtils";

import ColorVisionResultCard from "../../common/ColorVisionResultCard";
import { ColorVisionTestResult } from "types/testTypes";
import { buildColorVisionMetrics } from "../utils/colorVisionMetrics";
import { useTranslation } from "react-i18next";

export default function ColorVisionTestEntry({ entry }) {
  const { t } = useTranslation();
  const testResults = entry as ColorVisionTestResult;
  const correctnessResults = toColorVisionCorrectnessArray(testResults?.results);
  const detailedResults = toColorVisionDetailedResults(testResults?.results);
  const booleanResults = correctnessResults.map((item) => item === true);
  const metrics = testResults?.results ? buildColorVisionMetrics(booleanResults) : null;
  const subtype = inferColorVisionSubtype(testResults?.results);
  const subtypeKeyMap = {
    protan: "colorVisionSummarySubtypeProtan",
    deutan: "colorVisionSummarySubtypeDeutan",
    unclassified: "colorVisionSummarySubtypeUnclassified",
    insufficient_data: "colorVisionSummarySubtypeInsufficientData",
  } as const;
  const statusColor =
    metrics?.screeningClass === "normal"
      ? "green"
      : metrics?.screeningClass === "deficiency"
        ? "red"
        : "yellow";

  return (
    <Stack gap={0} mb={"md"}>
      {metrics && (
        <Card withBorder radius="md" mb="md" p="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Stack gap="xs" style={{ flex: 1, minWidth: 260 }}>
                <Group justify="space-between">
                  <Title order={4}>{t("colorVisionSummaryTitle")}</Title>
                  <Stack gap={6} align="flex-end">
                    <Badge color={statusColor} variant="filled">
                      {t(metrics.screeningStatusKey)}
                    </Badge>
                    <Badge variant="light" color="blue">
                      {t("colorVisionSummaryConfidenceLabel")}: {metrics.confidencePercent}%
                    </Badge>
                  </Stack>
                </Group>
                <Progress
                  value={metrics.confidencePercent}
                  color={
                    metrics.confidencePercent >= 70
                      ? "green"
                      : metrics.confidencePercent >= 40
                        ? "yellow"
                        : "red"
                  }
                  radius="xl"
                  size="md"
                />
                <Text size="sm">
                  {t("colorVisionSummaryMainScore", {
                    correct: metrics.screening.correct,
                    total: metrics.screening.total,
                    errors: metrics.screening.errors,
                  })}
                </Text>
              </Stack>
            </Group>
            <SimpleGrid cols={4} spacing="sm">
              <Paper withBorder p="sm" radius="md">
                <Text size="sm" fw={700} c="dimmed">
                  {t("colorVisionSummaryMetricTransformationTitle")}
                </Text>
                <Text size="xs" fw={700} c="blue.7">
                  2-9
                </Text>
                <Text size="xl" fw={700} c="blue.7">
                  {metrics.transformation.correct}/{metrics.transformation.total}
                </Text>
                <Text size="xs" c="dimmed">
                  {t("colorVisionSummaryGroupTransformation", {
                    correct: metrics.transformation.correct,
                    total: metrics.transformation.total,
                  })}
                </Text>
              </Paper>
              <Paper withBorder p="sm" radius="md">
                <Text size="sm" fw={700} c="dimmed">
                  {t("colorVisionSummaryMetricVanishingTitle")}
                </Text>
                <Text size="xs" fw={700} c="green.7">
                  10-17
                </Text>
                <Text size="xl" fw={700} c="green.7">
                  {metrics.vanishing.correct}/{metrics.vanishing.total}
                </Text>
                <Text size="xs" c="dimmed">
                  {t("colorVisionSummaryGroupVanishing", {
                    correct: metrics.vanishing.correct,
                    total: metrics.vanishing.total,
                  })}
                </Text>
              </Paper>
              <Paper withBorder p="sm" radius="md">
                <Text size="sm" fw={700} c="dimmed">
                  {t("colorVisionSummaryMetricHiddenDigitTitle")}
                </Text>
                <Text size="xs" fw={700} c="yellow.8">
                  18-21
                </Text>
                <Text size="xl" fw={700} c="yellow.8">
                  {metrics.hidden.total > 0
                    ? `${metrics.hidden.correct}/${metrics.hidden.total}`
                    : t("colorVisionSummaryNotAdministered")}
                </Text>
                <Text size="xs" c="dimmed">
                  {metrics.hidden.total > 0
                    ? t("colorVisionSummaryGroupHiddenDigit", {
                        correct: metrics.hidden.correct,
                        total: metrics.hidden.total,
                      })
                    : t("colorVisionSummaryGroupNotAdministered")}
                </Text>
              </Paper>
              <Paper withBorder p="sm" radius="md">
                <Text size="sm" fw={700} c="dimmed">
                  {t("colorVisionSummaryMetricTypingCluesTitle")}
                </Text>
                <Text size="xs" fw={700} c="red.7">
                  22, 23, 24, 26
                </Text>
                <Text size="xl" fw={700} c="red.7">
                  {metrics.typing.total > 0
                    ? `${metrics.typing.correct}/${metrics.typing.total}`
                    : t("colorVisionSummaryNotAdministered")}
                </Text>
                <Text size="xs" c="dimmed">
                  {metrics.typing.total > 0
                    ? t("colorVisionSummaryGroupTypingClues", {
                        correct: metrics.typing.correct,
                        total: metrics.typing.total,
                      })
                    : t("colorVisionSummaryGroupNotAdministered")}
                </Text>
              </Paper>
            </SimpleGrid>
            <Group gap="xs">
              <Badge variant="light" color="blue">
                {t("colorVisionSummarySubtypeLabel")}
              </Badge>
              <Badge variant="filled" color="blue">
                {t(subtypeKeyMap[subtype])}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {t("colorVisionSummaryPlainIntro", {
                correct: metrics.screening.correct,
                total: metrics.screening.total,
              })}{" "}
              {t(metrics.screeningStatusKey)} {t(metrics.typeHintKey)}{" "}
              {t("colorVisionSummaryPlainDisclaimer")}
            </Text>
          </Stack>
        </Card>
      )}
      {testResults?.results && <ColorVisionResultCard results={detailedResults} cols={5} />}
    </Stack>
  );
}
